import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { clients } from "../config/clients";

const ClientLogo = ({ client, index }) => {
  return (
    <div
      className="flex-shrink-0 w-[200px] md:w-[240px] mx-4 md:mx-6 flex items-center justify-center"
      data-testid={`client-logo-${index}`}
    >
      <div className="bg-white rounded-xl px-6 py-8 md:px-8 md:py-10 w-full shadow-sm hover:shadow-md transition-shadow duration-300 flex items-center justify-center min-h-[100px]">
        <span className="text-[#444444] font-bold text-base md:text-lg text-center leading-tight">
          {client.name}
        </span>
      </div>
    </div>
  );
};

const ClientsCarousel = () => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const animationRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Duplicate clients for seamless infinite scroll
  const duplicatedClients = [...clients, ...clients];

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollSpeed = 0.4; // pixels per frame
    const totalWidth = container.scrollWidth / 2; // Half because we duplicated

    const animate = () => {
      if (!isPaused && container) {
        scrollPositionRef.current += scrollSpeed;
        
        // Reset to start when we've scrolled through the first set
        if (scrollPositionRef.current >= totalWidth) {
          scrollPositionRef.current = 0;
        }
        
        container.scrollLeft = scrollPositionRef.current;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused]);

  return (
    <section className="py-16 md:py-24 bg-[#fafafa] overflow-hidden" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-[#a5dae0] font-script text-2xl mb-4">Trusted By</p>
        </motion.div>
      </div>

      {/* Carousel Container */}
      <div
        ref={scrollRef}
        className="flex overflow-x-hidden cursor-grab py-4"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
        data-testid="clients-carousel"
      >
        {duplicatedClients.map((client, index) => (
          <ClientLogo
            key={`${client.name}-${index}`}
            client={client}
            index={index}
          />
        ))}
      </div>
    </section>
  );
};

export default ClientsCarousel;
